import { Button } from "@/common/ui/button";
import { Checkbox } from "@/common/ui/checkbox";

import { formatEntitlementValue, grantTierLabel } from "./effective";
import type { AuditLogEntry, AuditValueView } from "./entitlements-api";

export interface AuditLogPanelProps {
  readonly entries: readonly AuditLogEntry[];
  readonly isLoading: boolean;
  readonly error: string | null;
  /** Whether the service is still holding rows past the ones shown. */
  readonly hasMore: boolean;
  /** Rows the service read but could not render; 0 in the normal case. */
  readonly unreadable: number;
  onLoadMore(): void;
  /** Nothing is read until a scope is chosen; a capability is optional. */
  readonly hasScope: boolean;
  /** null when no capability is selected, which is when narrowing is unavailable. */
  readonly capabilityId: string | null;
  readonly narrowed: boolean;
  onNarrowedChange(next: boolean): void;
}

/**
 * The stored action verb. An unrecognized one is shown as itself rather than
 * mapped to a default: a row this console has not been taught about is still a
 * change somebody made, and labelling it "Set" would be a claim about a mutation
 * nobody here understands.
 */
function actionLabel(action: string): string {
  switch (action) {
    case "grant.set":
      return "Set";
    case "grant.clear":
      return "Cleared";
    case "bundle.apply":
      return "Bundle applied";
    case "bundle.retract":
      return "Bundle retracted";
    default:
      return action;
  }
}

function valueLabel(value: AuditValueView | null): string {
  return value === null ? "nothing set" : formatEntitlementValue(value.type, value.value);
}

function Entry({ entry }: { readonly entry: AuditLogEntry }): React.ReactElement {
  return (
    <li className="border-b py-3 last:border-b-0" data-testid="audit-log-entry">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
        <p className="text-sm font-medium">{entry.capabilityId}</p>
        <p className="text-xs text-muted-foreground">
          <time dateTime={entry.timestamp}>{new Date(entry.timestamp).toLocaleString()}</time>
        </p>
      </div>
      <p className="text-sm" data-testid="audit-log-change">
        {actionLabel(entry.action)}: {valueLabel(entry.before)} to {valueLabel(entry.after)}
      </p>
      {typeof entry.bundleName === "string" && (
        <p className="text-xs text-muted-foreground" data-testid="audit-log-offering">
          {/* No version on a retract, because a retract removes EVERY applied
              version of the offering and records none. An empty one here would
              read as a value this console failed to load.

              A row whose provenance is missing was WRITTEN before the write
              side stored `bundle_name` and `bundle_version`, which is where the
              nulls come from: not from any versioning of this read. Whoever
              debugs one needs to look at write-time attribute absence.

              Tested for a STRING rather than compared to null, because absent
              and null both mean "no offering here" and only one of them is
              null. The store maps a missing attribute to null, but this
              component reads JSON over a wire that can drop the key again: an
              admin-api predating the field, or a serializer set to omit nulls,
              sends no key at all. `undefined !== null` is true, so a null
              comparison paints "Offering undefined" on every row, direct
              grants included. */}
          Offering {entry.bundleName}
          {typeof entry.bundleVersion === "string" && ` v${entry.bundleVersion}`}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {grantTierLabel(entry.tier)} by {entry.actorId}
      </p>
    </li>
  );
}

/**
 * The append-only mutation log for the selected scope (ENR-1130), newest first.
 *
 * Read-only by construction, not by omission: this feature reaches the log
 * through a service whose role holds Query and PutItem on the audit table and no
 * verb that could change a row, so there is no edit or delete path to hide here.
 *
 * Presentational, like `ResolutionView`: the page owns the reading and the
 * narrowing toggle, so the panel cannot show one thing while the page reads
 * another.
 *
 * "Load more" follows the CURSOR rather than the size of the last page. Narrowing
 * to one capability is applied after a page is read, so a narrowed page can come
 * back empty with rows still behind it; hiding the button then would silently
 * truncate the history at the first quiet page.
 */
export function AuditLogPanel({
  entries,
  isLoading,
  error,
  hasMore,
  unreadable,
  onLoadMore,
  hasScope,
  capabilityId,
  narrowed,
  onNarrowedChange,
}: AuditLogPanelProps): React.ReactElement {
  if (!hasScope) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="audit-log-empty">
        Choose a scope to see what changed there.
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="audit-log-panel">
      {capabilityId !== null && (
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={narrowed}
            onCheckedChange={(next) => onNarrowedChange(next === true)}
            data-testid="audit-log-narrow"
          />
          Only {capabilityId}
        </label>
      )}

      {error !== null && (
        <p className="text-sm text-destructive" data-testid="audit-log-error">
          {error}
        </p>
      )}

      {entries.length > 0 && (
        <ul className="divide-y" data-testid="audit-log-entries">
          {entries.map((entry) => (
            <Entry key={entry.id} entry={entry} />
          ))}
        </ul>
      )}

      {isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="audit-log-loading">
          Reading the audit log
        </p>
      )}

      {unreadable > 0 && (
        <p className="text-sm text-destructive" data-testid="audit-log-unreadable">
          {unreadable} recorded {unreadable === 1 ? "change" : "changes"} could not be displayed.
          What is shown here is incomplete.
        </p>
      )}

      {!isLoading && error === null && entries.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="audit-log-none">
          {/* Narrowed, the scope may well have changes; none of them are to the
              capability being asked about. Saying otherwise describes the wrong
              thing as empty. */}
          {hasMore
            ? "No matching changes on this page."
            : narrowed && capabilityId !== null
              ? `No changes to ${capabilityId} have been recorded at this scope.`
              : "No changes have been recorded at this scope."}
        </p>
      )}

      {hasMore && !isLoading && (
        <Button variant="outline" size="sm" onClick={onLoadMore} data-testid="audit-log-more">
          Load more
        </Button>
      )}
    </div>
  );
}
